<template>
  <div class="lg:col-span-9 ml-1">
    <!-- Profile section -->
    <div class="py-6 lg:pb-8">
      <div class="">
        <p v-if="isClientTeam" class="mt-1 text-sm text-secondary-lighter">
          Wir versenden eine Einladung per Email an die angegebene Adresse. Hier eingeladene Benutzer können nur auf diesen Mandanten zugreifen.
        </p>
        <p v-else class="mt-1 text-sm text-secondary-light">
          Wir versenden eine Einladung per Email an die angegebene Adresse. Ihre
          Team-Mitglieder können auf alle Kunden und Einstellungen
          zugreifen.
        </p>
      </div>
      <div class="mt-6 grid grid-cols-12 gap-4">
        <!-- first name -->
        <div class="col-span-6">
          <input
            v-model="firstname"
            type="text"
            name="firstname"
            required
            class="text-input-standard"
            placeholder="Vorname"
          >
        </div>

        <!-- surname -->
        <div class="col-span-6">
          <input
            v-model="lastname"
            type="text"
            name="lastname"
            required
            class="text-input-standard"
            placeholder="Nachname"
          >
        </div>

        <!-- email -->
        <div class="col-span-12">
          <input
            v-model="email"
            type="email"
            name="email"
            required
            class="text-input-standard"
            placeholder="E-Mail"
          >
        </div>
        <div class="mt-2 col-span-12 flex justify-end">
          <button class="btn-standard !bg-lilac disabled:opacity-75" @click="addUser">
            Einladen
          </button>
        </div>
      </div>
      <!-- team list-->
      <div class="mt-10">
        <h3 v-if="users.length > 0" class="text-sm font-bold">
          Aktuelles Team
        </h3>
        <Spinner v-if="loading" class="text-lilac mt-5 mx-auto h-4 w-4" />
        <ul v-else role="list" class="mt-4 divide-y divide-secondary-light">
          <li
            v-for="user in users"
            :key="user.email"
            class="flex items-center justify-between space-x-3 py-4"
          >
            <div class="flex min-w-0 flex-1 items-center space-x-3">
              <div class="shrink-0">
                <div
                  :class="[
                    user.requiredActions.length === 0
                      ? 'bg-lilac'
                      : 'bg-lilac-light',
                  ]"
                  class="flex items-center text-common-white justify-center w-12 h-12 text-white text-sm rounded-md font-bold"
                >
                  {{ user.firstName.substring(0, 1) }}
                  {{ user.lastName.substring(0, 1) }}
                </div>
              </div>
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-bold text-white">
                  {{ user.firstName }} {{ user.lastName }}
                  {{
                    user.requiredActions.length !== 0
                      ? '(Einladung noch nicht akzeptiert)'
                      : ''
                  }}
                </p>
                <span class="truncate text-sm font-medium">
                  {{ user.email }}
                </span>
              </div>
            </div>
            <!-- hide delete on own user -->
            <div v-if="(currentUserSession.user.email !== user.email) && envVars.public.ENVIRONMENT !== 'demo'" class="shrink-0">
              <button
                class="inline-flex items-center rounded-lg border border-transparent py-1.5 px-2.5 bg-secondary-dark hover:bg-secondary-darker text-common-white"
                @click="handleDeleteUser(user)"
              >
                <BIconTrash3 class="w-3.5 h-3.5 mr-1.5 text-common-white" />
                <span class="text-sm font-medium">Entfernen </span>
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <InfoModal
      :show="showDeleteWarning"
      :text="`Soll der Benutzer ${userToDelete.email} wirklich gelöscht werden?`"
      titel="Benutzer löschen"
      button-confirm-text="Unwiderruflich löschen"
      @on-cancel="showDeleteWarning = false"
      @on-confirm="
        deleteUser();
        showDeleteWarning = false;
      "
    />
  </div>
</template>

<script setup lang='ts'>
import { BIconTrash3 } from 'bootstrap-icons-vue'
import Spinner from '~/components/shared/basics/Spinner.vue'
import InfoModal from '~/components/shared/modals/InfoModal.vue'
import { useCurrentClient } from '~/composables/useCurrentClient'
import { useCurrentServiceprovider } from '~/composables/useCurrentServiceprovider'

const { serviceprovider } = useCurrentServiceprovider()
const { getCurrentClientUUID } = useCurrentClient()
const { $toast, $logger, $api } = useNuxtApp()
const { currentUserSession, currentEnv } = useCurrentUserSession()
const envVars = useRuntimeConfig()

const loading = ref(true)
const email = ref('')
const lastname = ref('')
const firstname = ref('')
const users = ref([])
const showDeleteWarning = ref(false)
const userToDelete = ref({})

const props = defineProps({
  isClientTeam: {
    type: Boolean,
    default: false
  }
})

onMounted(() => { getUsers() })

function handleDeleteUser (user: any): void {
  if (currentEnv.isDemoEnv) {
    $toast.error('Demo Umgebung', 'Benutzer können in der Demo Umgebung nicht gelöscht werden')
    return
  }

  userToDelete.value = user
  showDeleteWarning.value = true
}

async function deleteUser (): Promise<void> {
  try {
    if (props.isClientTeam) {
      await $api.client.removeClientTeamMember(getCurrentClientUUID(), {
        userUUID: userToDelete.value.id
      })
    } else {
      await $api.serviceprovider.removeServiceproviderTeamMember(serviceprovider.value.uuid, {
        userUUID: userToDelete.value.id
      })
    }

    $toast.success(`Benutzer ${userToDelete.value.email} wurde gelöscht`)

    clearForm()
    await getUsers()
  } catch (e) {
    $logger.error(e)
    $toast.error(e)
  }
}

const areRequiredFieldsFilled = computed(() => {
  return !(email.value.length === 0 || lastname.value.length === 0 || firstname.value.length === 0)
})

async function addUser (): Promise<void> {
  try {
    if (currentEnv.isDemoEnv) {
      $toast.error('Demo Umgebung', 'Benutzer können in der Demo Umgebung nicht hinzugefügt werden')
      return
    }

    // check required fields
    if (areRequiredFieldsFilled.value === false) {
      $toast.error('Fehler', 'Bitte alle Pflichtfelder ausfüllen')
      return
    }

    // Keycloak name validation
    if (!isValidKeycloakName(firstname.value) || !isValidKeycloakName(lastname.value)) {
      $toast.error('Fehler', 'Vorname oder Nachname enthält ungültige Zeichen')
      return
    }

    if (props.isClientTeam) {
      await $api.client.addClientTeamMember(getCurrentClientUUID(), {
        firstname: firstname.value,
        lastname: lastname.value,
        email: email.value
      })
    } else {
      await $api.serviceprovider.addServiceproviderTeamMember(serviceprovider.value.uuid, {
        firstname: firstname.value,
        lastname: lastname.value,
        email: email.value
      })
    }

    $toast.success(`Benutzer ${email.value} wurde erfolgreich eingeladen`)

    clearForm()
    await getUsers()
  } catch (e) {
    let res = null
    if (e.response) {
      res = await e.response.json()
    }
    if (res && res.message === 'User exists with same email') {
      $toast.error('Fehler', 'Benutzer ist bereits einer anderen Domäne zugeordnet')
    } else if (res && res.message) {
      $toast.error('Fehler', res.message)
    } else {
      $logger.error(e)
      $toast.error('Fehler', 'Der Benutzer konnte nicht hinzugefügt werden')
    }
  }
}

function clearForm (): void {
  email.value = ''
  lastname.value = ''
  firstname.value = ''
  userToDelete.value = {}
}

async function getUsers (): Promise<void> {
  try {
    let usersRes
    if (props.isClientTeam) {
      usersRes = await $api.client.getClientTeam(getCurrentClientUUID())
    } else {
      usersRes = await $api.serviceprovider.getServiceProviderTeam(serviceprovider.value.uuid)
    }

    users.value = usersRes
    loading.value = false // show screen to user
  } catch (e) {
    if (e.response) {
      $toast.error('Fehler', 'Team konnte nicht geladen werden')
      $logger.error(new Error(`Error loading team members ${e}`))
    }
  }
}

function isValidKeycloakName (name: string): boolean {
  // Matches any string that does NOT contain prohibited characters
  const prohibitedPattern = /^[^<>&"\v$%!#?§;*~\/\\|^=\[\]{}()]+$/u
  return prohibitedPattern.test(name)
}
</script>
<style scoped></style>
